import type { TFunction } from 'i18next'
import { ArrowLeft, ChevronDown, Clock } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useFormat } from '@/i18n/useFormat'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  legDraftFrom,
  legDraftIncomplete,
  toLegPayload,
  useRemoveLeg,
  useUpdateLeg,
} from '@/features/cases/api/useCase'
import {
  useApplyDirectionTemplate,
  useCaseLeg,
  useReplaceLegPoints,
} from '@/features/cases/api/useCaseLeg'
import { LegFields, type LegDraft } from '@/features/cases/components/CaseRecordSheets'
import {
  LegDraftDesk,
  LegFinance,
  LegPlace,
  PointTimeline,
  RouteEditor,
  VehiclesTable,
  chainWithReachedCount,
  legIdentityHidden,
  overdue,
  overdueDays,
} from '@/features/cases/components/legs'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { RouteOverview } from '@/features/cases/components/legs/RouteOverview'
import { useDirectionTemplates } from '@/features/settings/api/useDirectionTemplates'
import { isSuppressed } from '@/types/domain'
import type { CaseLeg } from '@/types/domain'

/* One direction — «Направление» — on its own page, at
 * `/cases/:caseId/legs/:legId`.
 *
 * ── Why this is a page and not the sheet it replaces ───────────────────────
 *
 * A leg used to be a 440px modal over the case's list. What changed is not the
 * amount of form: it is that a direction now carries a ROUTE CHAIN — an ordered
 * list of points, where the cargo has got to on it, and when the next transition
 * is due — and a chain is read ACROSS. Three things have to be in view together
 * for the work to be possible: where the cargo is, what the transport report
 * says (the status), and the dates that must agree with both. A column holds
 * one of them.
 *
 * It also makes a direction a URL. «Посмотри направление 2» is the sentence
 * Operations actually says, and until now the only way to honour it was "open
 * the case and click the second card".
 *
 * ── The chain is the position; the status is the report ────────────────────
 *
 * Those are two facts and they stay two controls. A leg can be `waiting-border`
 * while the chain's last reached point is Almaty — the truck is standing at a
 * crossing it has not been cleared through — and a single field answering both
 * questions would be wrong in one of them every time. The five `LegStatus`
 * values are untouched by this page.
 *
 * ── Nothing on this page decides where the cargo is ────────────────────────
 *
 * `currentPointId` and `nextPointDueAt` are SERVED. The chain marks them, the
 * overdue strip compares one timestamp against the clock, and that is the whole
 * of this side's arithmetic. Re-deriving the current point from the arrivals
 * would be the second definition of a shipment's position that `legnow` exists
 * to prevent — and this is the surface where being wrong is most expensive,
 * because it is the one somebody reads out to a client.
 */

export function CaseLegPage() {
  const { caseId = '', legId = '' } = useParams()

  /* `legs/new` is this same route with the literal in the param, not a route of
     its own (see `routes/index.tsx`). That is what makes the create's
     `navigate(replace)` a PARAM change rather than a swap of route elements, so
     this component stays mounted across it — which is the only reason the apply
     below can be armed here and survive. */
  const drafting = legId === 'new'

  /* ONE instance, above both desks. The draft desk fires it from the create's
     answer and is unmounted an instant later by the navigation; the leg desk
     renders its refusal. Two instances — which is what two route components
     gave — meant a refused copy had nowhere to print, and the reader landed on
     an empty chain with no sentence anywhere. */
  const applyTemplate = useApplyDirectionTemplate(caseId)

  const { data, isPending, isError, error, refetch } = useCaseLeg(
    caseId,
    drafting ? '' : legId,
  )
  const retry = () => void refetch()

  /* The way back is rendered on ALL branches, including the failures. A page
     reached by URL that answers 403 or 404 with nothing but an error box is a
     dead end: the reader has no way back to the case except the browser's own
     button, and on a phone opened from a chat message there is none. */
  return (
    <div className="flex min-w-0 flex-col gap-5 pb-24 md:pb-0">
      <BackToCase caseId={caseId} />

      {drafting ? (
        <LegDraftDesk caseId={caseId} applyTemplate={applyTemplate} />
      ) : isPending ? (
        <>
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-80 rounded-[var(--radius-DEFAULT)]" />
        </>
      ) : isError ? (
        <ErrorState error={error} onRetry={retry} />
      ) : (
        /* `onRetry` reaches ONE block: the money, which the composer can fail
           to assemble while the leg itself reads perfectly (`moneyUnavailable`).
           The rest of the page has nothing to retry — it either rendered or the
           whole read failed above. */
        <LegDesk
          caseId={caseId}
          leg={data}
          onRetryMoney={retry}
          applyTemplate={applyTemplate}
        />
      )}
    </div>
  )
}

/** The link back to where the direction was opened from.
 *
 *  `?section=legs` rather than the bare case: the reader came from that section
 *  and is going back to it, and landing on the case's Overview would make them
 *  find their way back to the list they were just in. */
function BackToCase({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  return (
    <Link
      to={`/cases/${caseId}?section=legs`}
      className="text-text-link hover:text-text-link-hover flex w-fit items-center gap-1.5 text-[13px]"
    >
      <ArrowLeft size={13} aria-hidden />
      {t('cases.legPage.back')}
    </Link>
  )
}

function LegDesk({
  caseId,
  leg,
  onRetryMoney,
  applyTemplate,
}: {
  caseId: string
  leg: CaseLeg
  onRetryMoney: () => void
  /** Armed by the page, and possibly ALREADY IN FLIGHT when this desk first
   *  mounts: the create fires it and navigates here in the same tick. */
  applyTemplate: ReturnType<typeof useApplyDirectionTemplate>
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const navigate = useNavigate()

  const [applying, setApplying] = useState(false)
  const [editing, setEditing] = useState(false)
  /* The chain's own edit mode, kept apart from `editing` above: one rewrites the
     leg RECORD and the other the ROUTE, they submit to two different endpoints,
     and a single flag would open both forms over one another. */
  const [editingRoute, setEditingRoute] = useState(false)
  const [routeExpanded, setRouteExpanded] = useState(false)

  const points = leg.points ?? []
  const replacePoints = useReplaceLegPoints(caseId, leg.id)
  const removeLeg = useRemoveLeg(caseId)

  /* The chain's normative days are what the automation runs on, so their
     presence is what the «Авто» tag reports — and it reports a FACT about this
     leg rather than a setting somebody switched. A chain typed by hand with no
     day counts does not advance by itself, and a tag claiming otherwise would
     have the desk waiting for a move that is never coming. */
  const automated = points.some((point) => point.plannedDaysFromPrevious !== undefined)

  const currentIndex = points.findIndex((point) => point.id === leg.currentPointId)
  const next = currentIndex >= 0 ? points[currentIndex + 1] : undefined
  const late = overdue(leg.nextPointDueAt)

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-text-heading text-[24px] leading-tight font-semibold tracking-[-0.5px] sm:text-[28px]">
              <LegPlace name={leg.origin} country={leg.originCountry} />
              <span className="text-text-faint mx-1.5" aria-hidden>
                →
              </span>
              <LegPlace name={leg.destination} country={leg.destinationCountry} />
            </h1>
            <StatusPill tone={STATUS_TONE[leg.status]}>
              {t(`legStatus.${leg.status}`)}
            </StatusPill>
          </div>

          <div className="text-text-muted flex items-center gap-2 text-[13px]">
            <span>{t('cases.legs.legN', { n: leg.sequence })}</span>
            {automated ? <Tag>{t('cases.legPage.autoTag')}</Tag> : null}
          </div>

          {/* Who is moving it, on what paper, and the two dates — the sentence
              Operations says on the phone, in that order. Each clause is
              dropped rather than dashed out when there is nothing to say: a
              subline reading "Перевозчик — · — · —" is four separators of
              nothing on a leg that has simply not been filled in yet.

              The carrier goes through `isSuppressed` rather than
              `SuppressedValue`, and the choice follows the shape of the line
              rather than a preference: this is ONE string built from four
              clauses and joined, so an element cannot be one of them. What
              matters is that the withheld marker is truthy — interpolating it
              raw printed «Перевозчик [object Object]» to every Sales caller who
              opened a direction that had a haulier (AD-6). */}
          {carrierClause(leg, t) || leg.reference ? <p className="text-text-muted text-[13px] leading-snug">
            {[
              carrierClause(leg, t),
              leg.reference
                ? `${leg.mode === 'road' ? t('cases.legs.cmr') : t('cases.legs.waybill')} ${leg.reference}`
                : null,
            ]
              .filter((clause) => clause !== null)
              .join(' · ')}
          </p> : null}
          <dl className="text-text-muted mt-2 flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
            {(['etd', 'eta', 'atd', 'ata'] as const).map((field) => leg[field] ? (
              <div key={field} className="flex gap-1.5">
                <dt>{t(`cases.legPage.dateLabels.${field}`)}</dt>
                <dd className="text-text-body font-medium">{format.dateShort(leg[field])}</dd>
              </div>
            ) : null)}
          </dl>
        </div>

        {/* ONE instance, placed once. On a phone it detaches into the bar
            pinned to the bottom of the viewport (UX-DR-27); on a desktop it
            sits beside the title. Rendering it twice would give a screen reader
            two "Изменить" buttons and a test two matches for one control. */}
        <div
          className={cn(
            'flex shrink-0 gap-2',
            'max-md:bg-surface max-md:border-border-subtle max-md:fixed',
            'max-md:inset-x-0 max-md:bottom-0 max-md:z-30 max-md:border-t',
            'max-md:px-4 max-md:py-3',
          )}
        >
          {/* «Применить направление» left this row for the route card's own
              header, beside «Изменить маршрут»: both write the chain, through
              the same `replacePoints` on the server, and a page-level control
              for one of them put two halves of one act in two places. What
              stays here edits the leg RECORD, which is what this header is
              about. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setEditing((open) => !open)}
            aria-expanded={editing}
          >
            {t('cases.legs.edit')}
          </Button>
        </div>
      </div>

      {editing ? (
        <LegEditCard caseId={caseId} leg={leg} onDone={() => setEditing(false)}
          onDeleted={() => void navigate(`/cases/${caseId}?section=legs`)} remove={removeLeg} />
      ) : null}

      {editingRoute ? (
        <RouteEditor
          legId={leg.id}
          points={points}
          pending={replacePoints.isPending}
          error={
            replacePoints.error ? pointsRefusal(replacePoints.error, t) : undefined
          }
          /* The refusal is dropped on the way OUT, not on the way in: a reader
             who gave up on a rejected chain must not find that sentence waiting
             over the timeline they went back to. */
          onCancel={() => {
            replacePoints.reset()
            setEditingRoute(false)
          }}
          onSubmit={(payload) =>
            replacePoints.mutate(payload, { onSuccess: () => setEditingRoute(false) })
          }
        />
      ) : (
      <Card>
        <CardHeader
          /* Both route writes, together. Applying a corridor and typing one by
             hand are the same act at two levels of effort, and the ghost/
             secondary pairing says which is the ordinary one. */
          className="flex-wrap gap-3 px-5 pt-5 sm:px-6"
          action={
            <div className="flex flex-wrap gap-2">
              {points.length >= 2 ? (
                <Button variant="ghost" size="sm" aria-expanded={routeExpanded}
                  aria-controls={`route-points-${leg.id}`} onClick={() => setRouteExpanded(!routeExpanded)}>
                  {t('cases.legPage.allPoints', { count: points.length })}
                  <ChevronDown size={15} aria-hidden className={cn('ml-1 transition-transform', routeExpanded && 'rotate-180')} />
                </Button>
              ) : null}
              {points.length < 2 || routeExpanded ? (
              <Button variant="ghost" size="sm" onClick={() => setApplying(true)}>
                {t('cases.legPage.applyTemplate')}
              </Button>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditingRoute(true)}
              >
                {t('cases.legPage.routeEdit')}
              </Button>
            </div>
          }
        >
          <CardTitle className="text-[18px] font-semibold">{t('cases.legPage.routeTitle')}</CardTitle>
        </CardHeader>
        <CardBody className="px-5 py-5 sm:px-6">
          {points.length < 2 ? (
            /* Not an EmptyState card: the direction itself exists and the rest
               of this page is about it. This is one sentence saying which
               controls fill the gap — both of them are in the header above. */
            <p className="text-text-muted text-[13px] leading-snug font-light">
              {t('cases.legPage.routeEmpty')}
            </p>
          ) : (
            routeExpanded ? <div id={`route-points-${leg.id}`} className="max-w-3xl">
            <p className="text-text-muted mb-4 text-[13px]">{t('cases.legPage.routeSubtitle')}</p>
            <PointTimeline
              points={points}
              currentPointId={leg.currentPointId}
              nextPointDueAt={leg.nextPointDueAt}
              /* Read for ONE refusal — a departed leg's origin arrival is the
                 server's to hold — and for nothing else. See `PointTimeline`. */
              status={leg.status}
              pending={replacePoints.isPending}
              onMarkThrough={(count) =>
                replacePoints.mutate(chainWithReachedCount(points, count))
              }
            />
            </div> : <RouteOverview leg={leg} />
          )}

          {/* The overdue strip. QUIET — a hairline row rather than an amber
              banner — because the transition running over is the ORDINARY
              course of events on a corridor with a normative time: the position
              moves by itself when it does, and the strip exists to offer the
              one correction only a human can make. An alarm on every second
              leg is an alarm nobody reads. */}
          {late && next ? (
            <div className="border-line-hair mt-4 flex flex-wrap items-center gap-3 border-t pt-3">
              <Clock size={15} aria-hidden className="text-text-muted shrink-0" />
              <p className="text-text-body min-w-0 flex-1 text-[12.5px] leading-snug">
                {t('cases.legPage.overdueBanner', {
                  count: overdueDays(leg.nextPointDueAt),
                  from: points[currentIndex]?.name ?? leg.origin,
                  to: next.name,
                })}
              </p>
              <Button
                variant="secondary"
                size="sm"
                disabled={replacePoints.isPending}
                /* `currentIndex + 1` is `next`'s own index, so this is the same
                   prefix the disc beside it submits — one definition of "the
                   cargo got this far", reached from two controls. */
                onClick={() =>
                  replacePoints.mutate(
                    chainWithReachedCount(points, currentIndex + 2),
                  )
                }
              >
                {t('cases.legPage.cargoAtNext', { point: next.name })}
              </Button>
            </div>
          ) : null}

          {/* The refusal lands under the chain that produced it. The prefix
              rule is the one worth naming in words: a chain cannot record an
              arrival at point 4 while point 3 has none, and the server says so
              on the field rather than quietly closing the gap. */}
          {replacePoints.error ? (
            <p className="text-negative mt-3 text-[12.5px] leading-snug">
              {pointsRefusal(replacePoints.error, t)}
            </p>
          ) : null}

          {/* A refused template copy, when the sheet that would normally hold
              the sentence is not open — which is the state the reader arrives in
              when the create fired the apply and sent them here. Without this
              the chain is simply empty and nothing on the page says why the
              corridor they chose is missing. `role="alert"` because it appears
              after the page has already rendered. */}
          {applyTemplate.error && !applying ? (
            <p role="alert" className="text-negative mt-3 text-[12.5px] leading-snug">
              {refusalMessage(applyTemplate.error, t)}
            </p>
          ) : null}
        </CardBody>
      </Card>
      )}

      {/* The trucks with their rates, then what has actually been paid for
          them. In that order and never the reverse: the money is arithmetic
          over the rows above it, and a card of figures over a table that
          explains none of them is the shape the Excel workbook had. */}
      <VehiclesTable caseId={caseId} leg={leg} />

      <LegFinance caseId={caseId} leg={leg} onRetry={onRetryMoney} />

      <ContainersCard leg={leg} />

      <ApplyTemplateSheet
        open={applying}
        onClose={() => setApplying(false)}
        pending={applyTemplate.isPending}
        error={applyTemplate.error ? refusalMessage(applyTemplate.error, t) : undefined}
        onApply={(templateId) =>
          applyTemplate.mutate(
            { legId: leg.id, templateId },
            { onSuccess: () => setApplying(false) },
          )
        }
      />
    </>
  )
}

/** «Перевозчик KTZ Ekspress», «Перевозчик скрыт», or nothing at all.
 *
 *  Three cases and three sentences, because the middle one is a fact rather than
 *  a blank: a Sales caller may not read who carries a leg (FR-29), and the
 *  server says so with the AD-6 marker rather than by omitting the key. A blank
 *  would read as a leg nobody has assigned yet and send somebody to fix it. */
function carrierClause(leg: CaseLeg, t: TFunction): string | null {
  if (isSuppressed(leg.carrierName)) {
    return `${t('cases.legs.carrier')} ${t('common.suppressed.carrier')}`
  }
  if (!leg.carrierName) return null
  return `${t('cases.legs.carrier')} ${leg.carrierName}`
}

const STATUS_TONE = {
  planned: 'neutral',
  'in-transit': 'brand',
  'waiting-border': 'attention',
  arrived: 'positive',
  completed: 'positive',
} as const

/* `reachedThrough` lived here and is now `chainWithReachedCount` in
   `components/legs/routeChain.ts`, shared with the timeline and the route
   editor. It did not merely move: it OR'd each point's existing arrival back
   into the submission, so no chain this client could compose ever cleared one,
   and a mis-recorded arrival was permanent. See the helper's own note. */

/** A refusal from the chain write, in words.
 *
 *  The prefix violation gets its own sentence rather than the generic
 *  "проверьте поля": it is the one refusal here a reader can act on, and the
 *  action ("mark the earlier point too") is not guessable from a field path. */
function pointsRefusal(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors ?? {}
    const notContiguous = Object.entries(fields).some(
      ([field, codes]) => field.endsWith('.reached') && codes.includes('not-contiguous'),
    )
    if (notContiguous) return t('cases.legPage.notContiguous')
  }
  return refusalMessage(error, t)
}

/** The boxes on this direction.
 *
 *  The trucks left this card for `VehiclesTable` (story 6), which edits them
 *  and shows what each one costs. The containers stayed, and the split is not
 *  arbitrary: a box is a read-only fact off the transport document with no
 *  money against it, where a truck now carries a carrier, a driver and a rate.
 *  Folding the two back together would put a twelve-column editor and a list of
 *  seal numbers under one heading.
 *
 *  Rendered only when there ARE boxes: road legs frequently have none, and an
 *  empty card on every one of them would be a heading over nothing. */
function ContainersCard({ leg }: { leg: CaseLeg }) {
  const { t } = useTranslation()
  const containers = leg.containers ?? []
  if (containers.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cases.legs.containers')}</CardTitle>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {containers.map((box) => (
            <li key={box.id} className="text-[12.5px] leading-snug">
              <span className="text-text-body font-mono">{box.number}</span>
              <span className="text-text-faint mx-1" aria-hidden>
                /
              </span>
              {/* An absent seal says so rather than printing a blank: empty
                  means NOT YET SEALED or not yet known, never "unsealed". */}
              {box.sealNumber ? (
                <span className="text-text-body font-mono">{box.sealNumber}</span>
              ) : (
                <span className="text-text-muted font-light">
                  {t('cases.legs.sealNotRecorded')}
                </span>
              )}
              {box.type ? (
                <span className="text-text-muted font-light"> · {box.type}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

/** The whole direction, editable in place.
 *
 *  A CARD rather than the sheet this replaced, and it asks four questions the
 *  sheet never did — the actual departure, the actual arrival, where the leg
 *  was last seen and why it is standing still. Those four were served on every
 *  leg and writable nowhere, which was not merely a gap: the PUT behind this
 *  form replaces the WHOLE record, so a form that never sent `stallNote` was a
 *  form that CLEARED the dispatcher's note the next time somebody corrected a
 *  date. `LegFields` is shared with the create sheet for exactly that reason —
 *  one definition of what a leg is made of. */
function LegEditCard({
  caseId,
  leg,
  onDone,
  onDeleted,
  remove,
}: {
  caseId: string
  leg: CaseLeg
  onDone: () => void
  onDeleted: () => void
  remove: ReturnType<typeof useRemoveLeg>
}) {
  const { t } = useTranslation()
  const updateLeg = useUpdateLeg(caseId)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const [draft, setDraft, touched, markTouched] = useFormDraft<LegDraft>(
    leg.id,
    () => legDraftFrom(leg),
  )
  const invalid = legDraftIncomplete(draft)
  const failure = updateLeg.error ?? remove.error

  /* The same refusal `VehiclesTable` makes, on the same condition and in the
     same words — this form submits the same whole-record PUT, and it was the
     half of the pair that had no guard. `legDraftFrom` blanks a withheld
     carrier so the marker never reaches an input (AD-6), and the carrier
     control here is disabled; but the SUBMIT still carried the blank, and an
     absent `carrierId` on this payload means UNLINK. A Sales caller correcting
     an ETA therefore detached the haulier from the direction — and, since the
     trucks now ride this payload too, from every truck on it.

     DELETION is not blocked by it. Removing a direction destroys nothing this
     reader cannot see that the server would not destroy anyway, and the server
     is the authority on whether they may (AD-21); it is the silent ERASURE
     under a save that had to stop. */
  const identityHidden = legIdentityHidden(leg)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cases.legPage.editTitle')}</CardTitle>
        <CardSubtitle>{t('cases.legPage.editSubtitle')}</CardSubtitle>
      </CardHeader>
      <CardBody>
        {/* The refusal renders BESIDE the form with the draft intact — the
            whole point of editing in place. A save that failed silently is the
            failure this application's own invariants warn about, and on a leg
            the commonest refusal is the delete's: FR-67 names the costs
            attached to it, and those names are the control. */}
        {failure ? (
          <p className="text-negative mb-3 text-[12.5px] leading-snug">
            {refusalMessage(failure, t)}
          </p>
        ) : touched && invalid ? (
          <p className="text-negative mb-3 text-[12.5px] leading-snug">
            {t('common.form.fixFields')}
          </p>
        ) : null}

        {/* Why the save is unavailable, said above the fields rather than after
            a press that does nothing — and as prose beside the button rather
            than as a `blockedReason` tooltip, which would say the same sentence
            twice in the harder place to reach (frontend/README, `MarginPanel`'s
            case). */}
        {identityHidden ? (
          <p className="text-text-muted mb-3 text-[12.5px] leading-snug">
            {t('cases.legs.identityHidden')}
          </p>
        ) : null}

        <div className="flex flex-col gap-3.5">
          <LegFields
            draft={draft}
            setDraft={setDraft}
            touched={touched}
            record={leg}
            actuals
          />
        </div>

        <div className="border-line-hair mt-4 flex flex-wrap items-center gap-2 border-t pt-3.5">
          <Button
            size="sm"
            disabled={updateLeg.isPending || identityHidden}
            onClick={() => {
              markTouched()
              if (invalid || identityHidden) return
              updateLeg.mutate(
                { legId: leg.id, payload: toLegPayload(draft) },
                { onSuccess: onDone },
              )
            }}
          >
            {t('common.actions.save')}
          </Button>
          <Button variant="secondary" size="sm" onClick={onDone}>
            {t('common.actions.cancel')}
          </Button>

          {/* The dangerous action, at the far edge and behind two presses —
              off the muscle memory Save sits on, the rule the sheets follow. */}
          <div className="ml-auto">
            {confirmingDelete ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-text-body text-[12.5px] leading-snug">
                  {t('cases.legs.deleteConfirm')}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={remove.isPending}
                >
                  {t('common.actions.cancel')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(leg.id, { onSuccess: onDeleted })}
                >
                  {t('cases.legs.deleteConfirmAction')}
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="text-negative cursor-pointer text-[12.5px]"
              >
                {t('cases.legs.delete')}
              </button>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

/** Choosing a direction to fill the chain from — and warning first.
 *
 *  The warning is the control, not decoration. Applying OVERWRITES the chain,
 *  and what goes with it is the arrivals somebody has been recording: there is
 *  no undo, and the points are the record of where a client's cargo has been. */
function ApplyTemplateSheet({
  open,
  onClose,
  onApply,
  pending,
  error,
}: {
  open: boolean
  onClose: () => void
  onApply: (templateId: string) => void
  pending?: boolean
  error?: string
}) {
  const { t } = useTranslation()
  /* Only the LIVE directions: Settings keeps retired corridors maintainable,
     and a picker that offered one would fill a running leg from a route the
     desk has decided is no longer run. An already-applied chain is unaffected —
     the points were copied, not linked. */
  const { data: directions } = useDirectionTemplates({ activeOnly: true })
  const [templateId, setTemplateId] = useState('')
  const [openFor, setOpenFor] = useState(open)
  if (openFor !== open) {
    setOpenFor(open)
    setTemplateId('')
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.legPage.applyTemplate')}
      submitLabel={t('cases.legPage.applyTemplateConfirm')}
      pending={pending}
      disabled={templateId === ''}
      error={error}
      /* NOTICE rather than an error: nothing has gone wrong, and this is a
         standing statement about what the action does. Red on a warning
         somebody has not triggered yet is how a product teaches people to
         dismiss red. */
      notice={t('cases.legPage.applyTemplateWarning')}
      onSubmit={() => {
        if (templateId === '') return
        onApply(templateId)
      }}
    >
      <Field label={t('cases.legPage.directionTemplate')}>
        {(props) => (
          <Select
            value={templateId}
            onChange={setTemplateId}
            options={(directions ?? []).map((direction) => ({
              value: direction.id,
              label: direction.name,
              hint: t('settings.directions.pointCount', {
                count: direction.points.length,
              }),
            }))}
            placeholder={t('cases.legPage.directionTemplateNone')}
            {...props}
          />
        )}
      </Field>
    </FormSheet>
  )
}
